import React, { useState, useEffect, useMemo } from 'react';
import { View, StyleSheet, TouchableOpacity, Animated, Easing, Modal, ActivityIndicator } from 'react-native';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import { useTranslation } from 'react-i18next';
import moment from 'moment-timezone';

import Text from '../global/Text';
import Button from '../global/RoundedButton';
import theme from '../../constants/theme';
import fontMaker from '../../utils/font';
import { SCREEN_WIDTH } from '../../utils/screensize';
import type { ScheduleListItemData } from '../../typing/scheduleItem';

interface HomeCheckInModalProps {
  visible: boolean;
  item: ScheduleListItemData | null;
  isBusy: boolean;
  isOffline: boolean;
  profileColor: string;
  onClose: () => void;
  onConfirmCheckIn: () => void;
  onContinueDraft: () => void;
  onResetDraft: () => void;
  onModifyPendingUpload: () => void;
  onReviewPendingUpload: () => void;
  onViewReport: () => void;
}

const HomeCheckInModal: React.FC<HomeCheckInModalProps> = ({
  visible,
  item,
  isBusy,
  isOffline,
  profileColor,
  onClose,
  onConfirmCheckIn,
  onContinueDraft,
  onResetDraft,
  onModifyPendingUpload,
  onReviewPendingUpload,
  onViewReport
}) => {
  const { t } = useTranslation(['site', 'common', 'home', 'schedule']);
  const [slideUpValue] = useState(new Animated.Value(0));
  const [showModifyConfirm, setShowModifyConfirm] = useState(false);

  useEffect(() => {
    if (visible) {
      slideUpValue.setValue(0);
      setShowModifyConfirm(false);
      Animated.timing(slideUpValue, {
        toValue: 1,
        duration: 300,
        useNativeDriver: true,
        easing: Easing.ease
      }).start();
    }
  }, [visible]);

  const timeText = useMemo(() => {
    if (!item) return '';
    if (item.isStartAnytime) return t('home:newHome.startAnytime');

    const tz = item.siteTimezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
    const startMoment = moment.tz(item.scheduledDate, tz).startOf('day').add(item.startTime, 'minutes');
    let endMoment = moment.tz(item.scheduledDate, tz).startOf('day').add(item.endTime, 'minutes');

    // midnight-crossing: if end is before start, the schedule spans into the next day
    if (item.endTime < item.startTime) {
      endMoment = endMoment.add(1, 'day');
    }

    const offset = startMoment.format('Z');
    const dateFmt = moment(item.scheduledDate).format('D MMM YYYY');
    const startFmt = startMoment.locale('en').format('hh:mm A');
    const endFmt = endMoment.locale('en').format('hh:mm A');

    if (startMoment.isSame(endMoment, 'day')) {
      return `${dateFmt} ${startFmt} - ${endFmt} GMT${offset}`;
    }
    return `${dateFmt} ${startFmt} - ${endMoment.format('D MMM YYYY')} ${endFmt} GMT${offset}`;
  }, [item, t]);

  const statusText = useMemo(() => {
    if (!item) return null;
    if (item.isPendingUpload) return t('site:pendingUpload');
    if (item.draftSource === 'local' || item.draftSource === 'remote') return t('site:continueReport');
    return null;
  }, [item, t]);

  const completionText = useMemo(
    () => (item ? `${item.totalAnswered}/${item.totalQuestion} ${t('home:newHome.completed')}` : ''),
    [item, t]
  );

  const handleClose = () => {
    if (!isBusy) onClose();
  };

  if (!item) return null;

  // ---------------------------------------------------------------------------
  // Action buttons based on item state
  // ---------------------------------------------------------------------------
  const renderActionButton = () => {
    if (isOffline) {
      return <Text style={styles.footerTextOffline}>{t('site:outlet.offlineInfo')}</Text>;
    }

    if (isBusy) {
      return (
        <View style={styles.loading}>
          <ActivityIndicator size="small" color="#3cd070" />
        </View>
      );
    }

    if (item.isPendingUpload) {
      if (showModifyConfirm) {
        return (
          <View>
            <Text style={styles.modifyConfirmText}>{t('schedule:modifyDesc')}</Text>
            <View style={styles.draftButtonRow}>
              <Button
                onPress={() => setShowModifyConfirm(false)}
                title={t('common:no')}
                backgroundColor="#aaa"
                style={styles.draftButton}
              />
              <View style={styles.draftButtonDivider} />
              <Button
                onPress={() => {
                  setShowModifyConfirm(false);
                  onModifyPendingUpload();
                }}
                title={t('common:yes')}
                backgroundColor={profileColor}
                style={styles.draftButton}
              />
            </View>
          </View>
        );
      }
      return (
        <View style={styles.draftButtonRow}>
          <Button
            onPress={() => setShowModifyConfirm(true)}
            title={t('common:modify')}
            backgroundColor="#aaa"
            style={styles.draftButton}
          />
          <View style={styles.draftButtonDivider} />
          <Button
            onPress={onReviewPendingUpload}
            title={t('common:syncServer')}
            backgroundColor="#3cd070"
            style={styles.draftButton}
          />
        </View>
      );
    }

    if ((item.draftSource === 'remote' || item.draftSource === 'local') && item.status === 'in-progress') {
      return (
        <View style={styles.draftButtonRow}>
          <Button
            onPress={onResetDraft}
            title={t('common:reset')}
            backgroundColor="#ed5565"
            style={styles.draftButton}
          />
          <View style={styles.draftButtonDivider} />
          <Button
            onPress={onContinueDraft}
            title={t('common:continue')}
            backgroundColor={profileColor}
            style={styles.draftButton}
          />
        </View>
      );
    }

    // Completed → View Report (navigates to the Reports list page)
    if (item.status === 'completed') {
      return (
        <Button onPress={onViewReport} backgroundColor={profileColor} title={t('site:outlet.button.viewReport')} />
      );
    }

    // Not started / Overdue → Check-In
    return <Button onPress={onConfirmCheckIn} backgroundColor={profileColor} title={t('common:checkIn')} />;
  };

  const typeBadgeColor = item.scheduleType === 'scheduled' ? theme.colors.primary : theme.colors.nimblyPurple;
  const typeBadgeLabel =
    item.scheduleType === 'scheduled' ? t('home:newHome.scheduled') : t('home:newHome.adhoc');

  return (
    <Modal visible={visible} transparent animationType="fade">
      <View style={styles.root}>
        <TouchableOpacity style={styles.backdrop} activeOpacity={1} onPress={handleClose} />
        <Animated.View
          style={[
            styles.container,
            {
              transform: [
                {
                  translateY: slideUpValue.interpolate({
                    inputRange: [0, 1],
                    outputRange: [300, 0]
                  })
                }
              ]
            }
          ]}
        >
          {/* Close button */}
          <View style={styles.close}>
            <TouchableOpacity onPress={handleClose}>
              <Icon name="close" size={25} />
            </TouchableOpacity>
          </View>

          {/* Title */}
          <View style={styles.containerTitle}>
            <Text style={styles.titleText}>{t('site:footer.title')}</Text>
          </View>

          {/* Body */}
          <View style={styles.body}>
            {/* Schedule info */}
            <View style={styles.timeStatusContainer}>
              <Text style={styles.text} numberOfLines={2}>
                {item.questionnaireName}
              </Text>
              {statusText ? (
                <Text style={styles.boldText}>{statusText}</Text>
              ) : (
                <Text style={styles.timeText}>{timeText}</Text>
              )}
              <Text style={styles.completionText}>{completionText}</Text>
            </View>

            {/* Location + Type badge */}
            <View style={styles.locationRow}>
              <View style={styles.locationLeft}>
                <Icon name="map-marker" size={16} color={theme.colors.gray} />
                <Text style={styles.locationText} numberOfLines={1}>
                  {item.siteName}
                </Text>
              </View>
              <View style={[styles.typeBadge, { backgroundColor: typeBadgeColor }]}>
                <Text style={styles.typeBadgeText}>{typeBadgeLabel}</Text>
              </View>
            </View>

            {/* Multisite info */}
            {item.isMultiSite && (
              <View style={styles.multisiteContainer}>
                <Text style={styles.multisiteTitle}>{t('site:footer.multisiteTitle')}</Text>
                <Text style={styles.multisiteSubtitle}>{t('site:footer.multisiteSubtitle')}</Text>
              </View>
            )}

            {/* Action button */}
            {renderActionButton()}
          </View>
        </Animated.View>
      </View>
    </Modal>
  );
};

export default HomeCheckInModal;

const styles = StyleSheet.create({
  root: {
    backgroundColor: 'rgba(0,0,0,0.4)',
    flex: 1,
    width: SCREEN_WIDTH > 500 ? 768 : SCREEN_WIDTH,
    marginHorizontal: 'auto'
  },
  backdrop: {
    flex: 1
  },
  container: {
    position: 'absolute',
    bottom: 0,
    width: '100%',
    backgroundColor: '#fff',
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 5 },
    shadowOpacity: 0.36,
    shadowRadius: 6.68,
    elevation: 11
  },
  close: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    paddingTop: 15,
    paddingRight: 10,
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20
  },
  containerTitle: {
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 12
  },
  titleText: {
    fontSize: 14,
    lineHeight: 19,
    color: theme.styles.textDark.color,
    ...fontMaker({ weight: 'SemiBold' })
  },
  body: {
    backgroundColor: '#ffffff',
    padding: 20,
    paddingBottom: 38
  },
  timeStatusContainer: {
    alignItems: 'flex-start',
    marginBottom: 10
  },
  text: {
    fontSize: 14,
    lineHeight: 19,
    color: theme.styles.textDark.color,
    marginBottom: 10
  },
  timeText: {
    fontSize: 12,
    lineHeight: 18,
    color: theme.colors.gray
  },
  completionText: {
    fontSize: 12,
    lineHeight: 18,
    color: theme.colors.gray,
    marginTop: 4
  },
  boldText: {
    ...fontMaker({ weight: 'SemiBold' })
  },
  locationRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 16
  },
  locationLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
    marginRight: 8
  },
  locationText: {
    fontSize: 13,
    color: theme.colors.gray,
    marginLeft: 4,
    flex: 1
  },
  typeBadge: {
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 20
  },
  typeBadgeText: {
    fontSize: 12,
    color: '#fff',
    ...fontMaker({ weight: 'SemiBold' })
  },
  multisiteContainer: {
    marginBottom: 20
  },
  multisiteTitle: {
    fontSize: 18,
    lineHeight: 25,
    fontWeight: '500'
  },
  multisiteSubtitle: {
    marginTop: 10,
    fontSize: 14,
    lineHeight: 19,
    ...fontMaker({ weight: 'regular' })
  },
  loading: {
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 11
  },
  footerTextOffline: {
    ...fontMaker({ weight: 'SemiBold' }),
    textAlign: 'center',
    color: '#ed5565'
  },
  draftButtonRow: {
    flexDirection: 'row',
    justifyContent: 'space-between'
  },
  draftButton: {
    flex: 1
  },
  draftButtonDivider: {
    width: 12
  },
  modifyConfirmText: {
    ...fontMaker({ weight: 'Normal' }),
    fontSize: 13,
    color: theme.colors.gray,
    marginBottom: 12,
    textAlign: 'center'
  }
});
